import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './NotificationSettings.css';
import {
  notificationSettingsAPI,
  notificationEventsAPI,
  notificationRemindersAPI,
  NotificationSettings as NotificationSettingsType,
  ContractEvent,
  EventReminder,
} from '../services/api';
import { PlusIcon, TrashIcon, CheckIcon, CloseIcon, CalendarIcon, DollarSignIcon, TrendingUpIcon, ChevronDownIcon, ChevronUpIcon } from './Icons';
import {
  getBrowserNotificationPermission,
  requestBrowserNotificationPermission,
  showBrowserNotification
} from '../utils/browserNotification';

interface NotificationSettingsProps {
  documentId: number;
  locked?: boolean;
  onLockedAction?: () => void;
  onEnabledChange?: (enabled: boolean) => void;
}

export const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  documentId,
  locked = false,
  onLockedAction,
  onEnabledChange
}) => {
  const { t } = useTranslation('notifications');
  const categoryLabels = useMemo(() => ({
    validity: t('settings.categories.validity'),
    payments: t('settings.categories.payments'),
    adjustment: t('settings.categories.adjustment'),
    other: t('settings.categories.other', { defaultValue: 'Outros' }),
  }), [t]);
  const eventTypeLabels = useMemo(() => ({
    contract_end: t('settings.events.contractEnd'),
    notice_deadline: t('settings.events.priorNotice'),
    rent_due: t('settings.events.rentDue'),
    condominium_due: t('settings.events.condominiumDue', { defaultValue: 'Vencimento do condomínio' }),
    iptu_due: t('settings.events.iptuDue', { defaultValue: 'Vencimento do IPTU' }),
    rent_adjustment: t('settings.events.annualAdjustment'),
    insurance_renewal: t('settings.events.insuranceRenewal', { defaultValue: 'Insurance renewal' }),
    guarantee_expiry: t('settings.events.guaranteeExpiry', { defaultValue: 'Guarantee expiry' }),
    deposit_return: t('settings.events.depositReturn', { defaultValue: 'Devolução da caução' }),
    inspection: t('settings.events.inspection', { defaultValue: 'Vistoria agendada' }),
    renewal_notice: t('settings.events.renewalNotice', { defaultValue: 'Aviso de renovação' }),
    custom: t('settings.events.custom', { defaultValue: t('settings.events.contractEnd') }),
  }), [t]);

  const eventCategories = useMemo(() => ({
    [categoryLabels.validity]: ['contract_end', 'notice_deadline', 'renewal_notice', 'inspection'],
    [categoryLabels.payments]: ['rent_due', 'condominium_due', 'iptu_due', 'deposit_return'],
    [categoryLabels.adjustment]: ['rent_adjustment'],
    [categoryLabels.other]: ['insurance_renewal', 'guarantee_expiry', 'custom'],
  }), [categoryLabels]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const [settings, setSettings] = useState<NotificationSettingsType | null>(null);
  const [events, setEvents] = useState<ContractEvent[]>([]);
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission | null>(
    getBrowserNotificationPermission()
  );

  // Edit drawer state
  const [editingEvent, setEditingEvent] = useState<ContractEvent | null>(null);
  const [editReminders, setEditReminders] = useState<EventReminder[]>([]);
  const [showAddReminderForm, setShowAddReminderForm] = useState(false);
  const [newReminderDays, setNewReminderDays] = useState<string>('7');
  const [newReminderTime, setNewReminderTime] = useState<string>('09:00');
  const [newReminderType, setNewReminderType] = useState<'days_before' | 'on_day'>('days_before');

  useEffect(() => {
    loadNotificationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const loadNotificationData = async () => {
    setLoading(true);
    setError(null);

    // If locked, show mock/disabled state instead of loading real data
    if (locked) {
      setSettings({
        id: 0,
        document_id: documentId,
        notifications_enabled: false,
        enabled_channels: [],
      });
      setEvents([]);
      setLoading(false);
      onEnabledChange?.(false);
      return;
    }

    try {
      const [settingsRes, eventsRes] = await Promise.all([
        notificationSettingsAPI.getSettings(documentId),
        notificationEventsAPI.getEvents(documentId),
      ]);

      setSettings(settingsRes.data);
      const sanitizedEvents = (eventsRes.data || []).filter(
        (event) => !!event.event_date || !!event.event_day
      );
      setEvents(sanitizedEvents);
      onEnabledChange?.(settingsRes.data.notifications_enabled);
    } catch (err: any) {
      console.error('Error loading notification data:', err);
      setError(err.response?.data?.detail || t('settings.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMaster = async (enabled: boolean) => {
    if (locked && onLockedAction) {
      onLockedAction();
      return;
    }

    if (!settings) return;

    setSaving(true);
    try {
      const res = await notificationSettingsAPI.updateSettings({
        document_id: documentId,
        notifications_enabled: enabled,
      });
      setSettings(res.data);
      onEnabledChange?.(res.data.notifications_enabled);
      setError(null);
    } catch (err: any) {
      console.error('Error updating master toggle:', err);
      setError(err.response?.data?.detail || t('settings.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleChannel = async (channel: string) => {
    if (!settings) return;

    const currentChannels = settings.enabled_channels;
    let newChannels: string[];

    if (currentChannels.includes(channel)) {
      newChannels = currentChannels.filter(c => c !== channel);
    } else {
      newChannels = [...currentChannels, channel];
    }

    if (newChannels.length === 0 && settings.notifications_enabled) {
      setError(t('settings.channels.error'));
      return;
    }

    if (channel === 'in_app') {
      // Request browser permission for in-app notifications
      if (!('Notification' in window)) {
        setError(t('settings.channels.permissionDenied'));
        return;
      }
      if (Notification.permission === 'denied') {
        setError(t('settings.channels.permissionDenied'));
        return;
      }
      if (Notification.permission !== 'granted') {
        const result = await Notification.requestPermission();
        if (result !== 'granted') {
          setError(t('settings.channels.permissionDenied'));
          return;
        }
      }
    }

    setSaving(true);
    try {
      const res = await notificationSettingsAPI.updateSettings({
        document_id: documentId,
        enabled_channels: newChannels,
      });
      setSettings(res.data);
      setError(null);
    } catch (err: any) {
      console.error('Error updating channels:', err);
      setError(err.response?.data?.detail || t('settings.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEvent = async (eventId: number, enabled: boolean) => {
    setSaving(true);
    try {
      await notificationEventsAPI.updateEvent(documentId, eventId, { is_enabled: enabled });
      setEvents(events.map(e => e.id === eventId ? { ...e, is_enabled: enabled } : e));
      setError(null);
    } catch (err: any) {
      console.error('Error toggling event:', err);
      setError(err.response?.data?.detail || t('settings.error'));
    } finally {
      setSaving(false);
    }
  };

  // Debug/Test notification trigger - schedules notification for 5 seconds in the future
  // to test the real notification mechanism (same as scheduled notifications)
  // const handleSendTestNotification = async (eventId: number) => {
  //   setSaving(true);
  //   try {
  //     await notificationEventsAPI.sendTestNotification(documentId, eventId);
  //     setError(null);
  //     const successMsg = t('settings.testNotificationSent', { defaultValue: 'Notificação de teste enviada' });
  //     setError(successMsg);
  //     setTimeout(() => setError(null), 3000);
  //
  //     // Dispatch event to trigger fast polling in Navbar
  //     // Browser notification will be shown when polling detects the new notification
  //     window.dispatchEvent(new CustomEvent('testNotificationSent'));
  //   } catch (err: any) {
  //     console.error('Error sending test notification:', err);
  //     setError(err.response?.data?.detail || 'Falha ao enviar notificação de teste');
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  const handleOpenEditDrawer = (event: ContractEvent) => {
    setEditingEvent(event);
    setEditReminders([...event.reminders]);
    setShowAddReminderForm(false);
    setNewReminderDays('7');
    setNewReminderTime('09:00');
    setNewReminderType('days_before');
  };

  const handleCloseEditDrawer = () => {
    setEditingEvent(null);
    setEditReminders([]);
    setShowAddReminderForm(false);
  };

  const toggleEventExpansion = (eventId: number) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case categoryLabels.validity:
        return <CalendarIcon size={18} />;
      case categoryLabels.payments:
        return <DollarSignIcon size={18} />;
      case categoryLabels.adjustment:
        return <TrendingUpIcon size={18} />;
      default:
        return <CalendarIcon size={18} />;
    }
  };

  const handleAddReminder = async () => {
    if (!editingEvent) return;

    const daysValue = newReminderType === 'days_before' ? parseInt(newReminderDays) : null;

    if (newReminderType === 'days_before' && (isNaN(daysValue!) || daysValue! < 0)) {
      setError('Dias deve ser um número positivo');
      return;
    }

    setSaving(true);
    try {
      const res = await notificationRemindersAPI.addReminder(documentId, editingEvent.id, {
        days_before: daysValue!,
        time_of_day: newReminderTime,
      });

      const newReminder = res.data;
      setEditReminders([...editReminders, newReminder]);
      setEvents(events.map(e => {
        if (e.id === editingEvent.id) {
          return { ...e, reminders: [...e.reminders, newReminder] };
        }
        return e;
      }));

      // Reset form
      setShowAddReminderForm(false);
      setNewReminderDays('7');
      setNewReminderTime('09:00');
      setNewReminderType('days_before');
      setError(null);
    } catch (err: any) {
      console.error('Error adding reminder:', err);
      setError(err.response?.data?.detail || t('settings.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReminder = async (reminderId: number) => {
    if (!editingEvent || !window.confirm(t('settings.reminders.confirmDelete'))) {
      return;
    }

    setSaving(true);
    try {
      await notificationRemindersAPI.deleteReminder(documentId, reminderId);

      setEditReminders(editReminders.filter(r => r.id !== reminderId));
      setEvents(events.map(e => {
        if (e.id === editingEvent.id) {
          return { ...e, reminders: e.reminders.filter(r => r.id !== reminderId) };
        }
        return e;
      }));
      setError(null);
    } catch (err: any) {
      console.error('Error deleting reminder:', err);
      setError(err.response?.data?.detail || t('settings.error'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const formatEventDay = (day: number | null) => {
    if (!day) return null;
    return `todo dia ${day}`;
  };

  const getReminderChips = (reminders: EventReminder[]) => {
    const enabled = reminders.filter(r => r.is_enabled);
    return enabled.map(r => {
      if (r.days_before === null || r.days_before === 0) return 'no dia';
      if (r.days_before === 1) return '1d';
      return `${r.days_before}d`;
    });
  };

  const getNextOccurrence = (event: ContractEvent): string => {
    if (event.event_date) {
      return formatDate(event.event_date) || t('settings.events.dateUnavailable');
    }
    if (event.event_day) {
      return t('settings.events.monthlyDayLabel', { day: event.event_day });
    }
    return t('settings.events.dateUnavailable');
  };

  const getCategoryForEvent = (eventType: string): string => {
    for (const [category, types] of Object.entries(eventCategories)) {
      if (types.includes(eventType)) {
        return category;
      }
    }
    return 'Outros';
  };

  const groupedEvents = events.reduce((acc, event) => {
    const category = getCategoryForEvent(event.event_type);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(event);
    return acc;
  }, {} as Record<string, ContractEvent[]>);

  if (loading) {
    return (
      <div className="notification-settings-card">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t('settings.loading')}</p>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="notification-settings-card">
        <div className="error-container">
          <p>{t('settings.error')}</p>
          <button onClick={loadNotificationData} className="btn-retry">{t('settings.retry')}</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="notification-settings-card">
        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="close-error">×</button>
          </div>
        )}

        {/* Header with Master Toggle */}
        <div className="notification-settings-header">
          <div className="notification-settings-title-row">
            <div className="notification-settings-title-content">
              <h3>{t('settings.title')}</h3>
              <p className="notification-settings-subtitle">{t('settings.subtitle')}</p>
            </div>
            <div className="notification-status-toggle">
              <label className="toggle-switch-with-label">
                <input
                  type="checkbox"
                  checked={settings.notifications_enabled}
                  onChange={(e) => handleToggleMaster(e.target.checked)}
                  disabled={saving}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-label">
                  {settings.notifications_enabled ? t('settings.enabled') : t('settings.disabled')}
                </span>
              </label>
            </div>
          </div>

          {/* Channel Selection - Only show when enabled */}
          {settings.notifications_enabled && (
            <div className="notification-channels-compact">
              <label className="channel-option">
                <input
                  type="checkbox"
                  checked={settings.enabled_channels.includes('in_app')}
                  onChange={() => handleToggleChannel('in_app')}
                  disabled={saving}
                />
                <span>{t('settings.channels.inApp')}</span>
                {settings.enabled_channels.includes('in_app') && browserPermission !== 'granted' && (
                  <button
                    type="button"
                    className="btn btn-xs btn-secondary"
                    style={{ marginLeft: '8px', fontSize: '11px', padding: '2px 8px' }}
                    onClick={async () => {
                      const permission = await requestBrowserNotificationPermission();
                      setBrowserPermission(permission);
                      if (permission === 'granted') {
                        // Test notification to confirm it works
                        showBrowserNotification('Contrato Seguro', {
                          body: t('settings.testNotificationBody', { defaultValue: 'Notificações do navegador ativadas!' })
                        });
                      }
                    }}
                  >
                    {browserPermission === 'denied'
                      ? t('settings.channels.permissionDenied', { defaultValue: 'Bloqueado' })
                      : t('settings.channels.enableBrowser', { defaultValue: 'Ativar navegador' })}
                  </button>
                )}
                {settings.enabled_channels.includes('in_app') && browserPermission === 'granted' && (
                  <span style={{ marginLeft: '8px', color: '#10b981', fontSize: '11px' }}>✓</span>
                )}
              </label>
              <label className="channel-option">
                <input
                  type="checkbox"
                  checked={settings.enabled_channels.includes('email')}
                  onChange={() => handleToggleChannel('email')}
                  disabled={saving}
                />
                <span>{t('settings.channels.email')}</span>
              </label>
            </div>
          )}
        </div>

        {!settings.notifications_enabled ? (
          <div className="notification-settings-disabled">
            <p>{t('settings.enabledNote')}</p>
          </div>
        ) : (
          <>

            {/* Events List Grouped by Category */}
            {Object.keys(groupedEvents).length === 0 ? (
              <div className="no-events-state">
                <p>{t('dropdown.empty')}</p>
              </div>
            ) : (
              <div className="notification-events">
                {Object.entries(groupedEvents).map(([category, categoryEvents]) => (
                  <div key={category} className="event-category">
                    <div className="category-header-new">
                      <div className="category-icon">{getCategoryIcon(category)}</div>
                      <h5 className="category-title">{category}</h5>
                    </div>
                    {categoryEvents.map(event => {
                      const reminderChips = getReminderChips(event.reminders);
                      const isExpanded = expandedEvents.has(event.id);
                      return (
                        <div key={event.id} className={`event-row-compact ${!event.is_enabled ? 'event-disabled' : ''}`}>
                          {/* Compact Row - Always Visible */}
                          <div
                            className="event-compact-header"
                            onClick={() => toggleEventExpansion(event.id)}
                          >
                            <div className="event-compact-left">
                              <div className="event-compact-title">
                                {event.custom_label || (eventTypeLabels as Record<string, string>)[event.event_type] || event.event_type}
                                {event.confidence === 'low' && <span className="event-tag-estimated">{t('settings.events.estimated')}</span>}
                              </div>
                              {event.description && (
                                <div className="event-compact-description">
                                  {event.description}
                                </div>
                              )}
                              <div className="event-compact-date">
                                {getNextOccurrence(event)}
                              </div>
                            </div>
                            <div className="event-compact-right">
                              <label className="toggle-switch small" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={event.is_enabled}
                                  onChange={(e) => handleToggleEvent(event.id, e.target.checked)}
                                  disabled={saving}
                                />
                                <span className="toggle-slider"></span>
                              </label>
                              <button className="expand-button" type="button">
                                {isExpanded ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
                              </button>
                            </div>
                          </div>

                          {/* Expanded Details */}
                          {isExpanded && (
                            <div className="event-expanded-content">
                              {/* Reminder Chips */}
                              {reminderChips.length > 0 ? (
                                <div className="event-reminder-section">
                                  <span className="event-section-label">{t('settings.reminders.title')}:</span>
                                  <div className="event-reminder-chips">
                                    {reminderChips.map((chip, idx) => (
                                      <span key={idx} className="reminder-chip">{chip} {t('settings.reminders.before')}</span>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="event-reminder-section">
                                  <span className="event-section-label">{t('settings.reminders.noReminders')}</span>
                                </div>
                              )}

                              {/* Action Buttons */}
                              <div className="event-actions-row">
                                <button
                                  className="event-edit-link"
                                  onClick={() => handleOpenEditDrawer(event)}
                                  disabled={saving}
                                >
                                  {t('settings.reminders.edit')} →
                                </button>
                                {/* <button
                                  className="btn-send-test"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSendTestNotification(event.id);
                                  }}
                                  disabled={saving || !event.is_enabled}
                                  title="Envia uma notificação de teste em 10 segundos"
                                >
                                  Send now
                                </button> */}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Drawer Modal */}
      {editingEvent && (
        <div className="edit-drawer-overlay" onClick={handleCloseEditDrawer}>
          <div className="edit-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="edit-drawer-header">
              <h3>{t('settings.reminders.edit')}</h3>
              <button className="edit-drawer-close" onClick={handleCloseEditDrawer}>
                <CloseIcon size={20} />
              </button>
            </div>

            <div className="edit-drawer-content">
              {/* Event Summary at Top */}
              <div className="edit-drawer-summary">
                <p className="edit-drawer-summary-label">{t('settings.events.mainDate')}</p>
                <p className="edit-drawer-summary-date">
                  {formatDate(editingEvent.event_date) || formatEventDay(editingEvent.event_day) || t('settings.events.dateUnavailable')}
                </p>
              </div>

              {/* Reminders List */}
              <div className="edit-drawer-section">
                <h5>{t('settings.reminders.title')}</h5>
                {editReminders.length === 0 ? (
                  <p className="no-reminders-text">{t('settings.reminders.noReminders')}</p>
                ) : (
                  <div className="reminders-list-edit">
                    {editReminders.map(reminder => (
                      <div key={reminder.id} className="reminder-chip">
                        <span>
                          {reminder.days_before === null || reminder.days_before === 0
                            ? t('settings.reminders.onDayFull')
                            : reminder.days_before === 1
                            ? t('settings.reminders.oneDayBefore')
                            : t('settings.reminders.daysBeforeFull', { count: reminder.days_before })}
                          {' '}
                          {reminder.time_of_day}
                        </span>
                        {reminder.is_default && <span className="default-badge-small">{t('settings.reminders.default')}</span>}
                        {!reminder.is_default && (
                          <button
                            className="reminder-chip-remove"
                            onClick={() => handleDeleteReminder(reminder.id)}
                            disabled={saving}
                          >
                            <TrashIcon size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Reminder Form */}
              {showAddReminderForm ? (
                <div className="edit-drawer-section add-reminder-section">
                  <h5>{t('settings.reminders.add')}</h5>
                  <div className="add-reminder-form">
                    <div className="form-row">
                      <label>
                        <input
                          type="radio"
                          value="days_before"
                          checked={newReminderType === 'days_before'}
                          onChange={() => setNewReminderType('days_before')}
                        />
                        {t('settings.reminders.daysBeforeLabel')}
                      </label>
                      <label>
                        <input
                          type="radio"
                          value="on_day"
                          checked={newReminderType === 'on_day'}
                          onChange={() => setNewReminderType('on_day')}
                        />
                        {t('settings.reminders.onDayLabel')}
                      </label>
                    </div>

                    {newReminderType === 'days_before' && (
                      <div className="form-group">
                        <label>{t('settings.reminders.howManyDays')}</label>
                        <input
                          type="number"
                          min="0"
                          value={newReminderDays}
                          onChange={(e) => setNewReminderDays(e.target.value)}
                          className="form-input"
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label>{t('settings.reminders.timeLabel')}</label>
                      <input
                        type="time"
                        value={newReminderTime}
                        onChange={(e) => setNewReminderTime(e.target.value)}
                        className="form-input"
                      />
                    </div>

                    <div className="form-actions">
                      <button
                        className="btn-primary-small"
                        onClick={handleAddReminder}
                        disabled={saving}
                      >
                        <CheckIcon size={16} /> {t('settings.reminders.save')}
                      </button>
                      <button
                        className="btn-secondary-small"
                        onClick={() => setShowAddReminderForm(false)}
                        disabled={saving}
                      >
                        {t('settings.reminders.cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  className="btn-add-reminder"
                  onClick={() => setShowAddReminderForm(true)}
                  disabled={saving}
                >
                  <PlusIcon size={16} /> {t('settings.reminders.add')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
